import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { PriceSource } from "../../api/client";
import { CategorySelectField } from "./CategoryPicker";
import { buildCategoryOptions, buildPriceSections } from "./category-model";

type PriceLoadState = "loading" | "ready" | "error";

export function PriceScreen({ loadPrice }: { loadPrice: (signal?: AbortSignal) => Promise<PriceSource> }) {
  const [source, setSource] = useState<PriceSource | null>(null);
  const [query, setQuery] = useState("");
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [state, setState] = useState<PriceLoadState>("loading");
  const controllerRef = useRef<AbortController | null>(null);

  const reload = useCallback(() => {
    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;
    setState("loading");
    void loadPrice(controller.signal).then(
      (nextSource) => {
        if (controller.signal.aborted) return;
        setSource(nextSource);
        setState("ready");
      },
      () => {
        if (controller.signal.aborted) return;
        setState("error");
      }
    );
  }, [loadPrice]);

  useEffect(() => {
    reload();
    return () => controllerRef.current?.abort();
  }, [reload]);

  const options = useMemo(
    () => source ? buildCategoryOptions(source, selectedCategories) : [],
    [selectedCategories, source]
  );
  const sections = useMemo(
    () => source ? buildPriceSections(source, selectedCategories, query) : [],
    [query, selectedCategories, source]
  );
  const sourceIsEmpty = state === "ready" && source?.items.length === 0;
  const noMatches = state === "ready" && !sourceIsEmpty && sections.length === 0;

  return <section className="screen-stack section-screen">
    <header><p className="eyebrow">Без вашей наценки</p><h1>Исходный прайс THLS</h1></header>
    <label className="search-field"><span aria-hidden="true">⌕</span><input type="search" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Найти товар" aria-label="Поиск по прайсу" /></label>
    <CategorySelectField options={options} value={selectedCategories} loading={state === "loading"} error={state === "error"} onRetry={reload} onApply={setSelectedCategories} />
    {state === "loading" && <div className="skeleton list-skeleton" aria-label="Загрузка прайса" />}
    {state === "error" && <div className="empty-state"><strong>Не удалось загрузить прайс</strong><p>Проверьте соединение и попробуйте ещё раз.</p><button type="button" className="price-retry" onClick={reload}>Повторить</button></div>}
    {sourceIsEmpty && <div className="empty-state">В прайсе пока нет товаров</div>}
    {noMatches && <div className="empty-state">Ничего не найдено.</div>}
    {state === "ready" && sections.map((section, index) => <section className="price-section" key={section.key} aria-labelledby={`price-section-${index}`}>
      <h2 id={`price-section-${index}`} className="price-category-heading"><span>{section.label}</span><small>{section.items.length} / {section.itemCount}</small></h2>
      <div className="price-list">
        {section.items.map((item, index) => <article key={`${item.categoryKey}:${item.name}:${index}`}><div>{item.countryCode && <small>{item.countryCode}</small>}<strong>{item.name}</strong></div><b>{item.priceRaw}</b></article>)}
      </div>
    </section>)}
  </section>;
}
